import { JsonPipe, NgClass } from '@angular/common';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, OnInit, output, signal, viewChild } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';

import { BaasAppState, BaasI18n } from '@lvflanders/bar';
import { BarI18nPipe } from '@lvflanders/bar-ng-ui';
import { DidoCoreStateService } from '@lvflanders/digitaaldossier-ng';

import { DIDO_DOCUMENTEN_CONTROL_CONSTANTS } from '../../dido-documenten-control.constants';
import { FileDropBehavior } from '../../directives/file-drop-behavior';
import { DocumentUploadModel } from '../../types/dido-document-upload-model.type';
import { DidoFileUploadRequest } from './dido-file-upload-request';

@Component({
    selector: 'dido-documenten-control-upload',
    templateUrl: './dido-documenten-control-upload.html',
    styleUrls: ['./dido-documenten-control-upload.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatCard, MatTooltip, MatCardContent, JsonPipe, NgClass, MatProgressSpinner, MatButton, FileDropBehavior, MatIcon, MatIconButton, BarI18nPipe]
})
/** Internal component which performs the upload */
export class DidoDocumentenControlUpload implements OnInit {
    readonly #http = inject(HttpClient);
    readonly #i18n = inject(BaasI18n);
    readonly #appState = inject(BaasAppState);
    readonly #state = inject(DidoCoreStateService);

    // reference to native file upload element
    readonly fileUpload = viewChild<ElementRef<HTMLInputElement>>('fileUpload');
    readonly bijlageUpload = viewChild<ElementRef<HTMLInputElement>>('bijlageUpload');

    // type van de upload: file - bijlage
    readonly typeUpload = input('file');
    // optional max file size
    readonly maxFileSize = input.required<number>();
    readonly multiple = input.required<boolean>();

    // whether disabled or enabled
    readonly disabled = input<boolean>();
    // This event is emitted when a file gets uploaded
    readonly uploaded = output<DocumentUploadModel[]>();
    readonly onUploadError = output<string[]>();
    readonly uploadButtonClicked = output<void>();

    // queue of requests
    protected readonly fileRequests = signal<DidoFileUploadRequest[]>([]);
    protected readonly dropzoneActive = signal(false);
    // accept extensions
    protected acceptExtensions: string;
    // whether intern or extern
    #intern = false;

    #maximumFileSizeBytes = computed(() => {
        return this.maxFileSize() || DIDO_DOCUMENTEN_CONTROL_CONSTANTS.DEFAULT_MAX_FILE_SIZE;
    });

    ngOnInit(): void {
        this.#intern = this.#appState.basiscontext.zone == 'I';
        let extensions = '';
        if (this.#intern) {
            for (let i = 0; i < DIDO_DOCUMENTEN_CONTROL_CONSTANTS.ALLOWED_EXTENSIONS_INTERN.length; i++) {
                extensions += DIDO_DOCUMENTEN_CONTROL_CONSTANTS.ALLOWED_EXTENSIONS_INTERN[i];
                if (i < DIDO_DOCUMENTEN_CONTROL_CONSTANTS.ALLOWED_EXTENSIONS_INTERN.length - 1) {
                    extensions += ', ';
                }
            }
        } else {
            for (let i = 0; i < DIDO_DOCUMENTEN_CONTROL_CONSTANTS.ALLOWED_EXTENIONS_EXTERN.length; i++) {
                extensions += DIDO_DOCUMENTEN_CONTROL_CONSTANTS.ALLOWED_EXTENIONS_EXTERN[i];
                if (i < DIDO_DOCUMENTEN_CONTROL_CONSTANTS.ALLOWED_EXTENIONS_EXTERN.length - 1) {
                    extensions += ', ';
                }
            }
        }

        this.acceptExtensions = extensions;
    }

    reset(): void {
        this.fileRequests.set([]);
        if (this.fileUpload() && this.fileUpload().nativeElement) {
            this.fileUpload().nativeElement.value = '';
        }
        if (this.bijlageUpload() && this.bijlageUpload().nativeElement) {
            this.bijlageUpload().nativeElement.value = '';
        }
    }

    protected dropzoneState($event: boolean) {
        this.dropzoneActive.set($event);
    }

    protected async handleDrop(fileList: FileList) {
        if (!this.multiple() && fileList && fileList.length > 1) {
            this.onUploadError.emit([this.#i18n.get('DOMAIN_DIDO.DOCUMENTEN_CONTROL.VALIDATION.MULTIPLE_FALSE')]);
        } else {
            await this.#handleChosenFiles(fileList);
        }
    }

    protected async onFilesSelected(): Promise<void> {
        let files;
        if (this.typeUpload() === 'file') {
            files = this.fileUpload().nativeElement.files;
        } else {
            files = this.bijlageUpload().nativeElement.files;
        }
        await this.#handleChosenFiles(files);
    }

    async #handleChosenFiles(files: FileList) {
        const fileRequests = [] as DidoFileUploadRequest[];

        // handle all files chosen by the user and queue them as file requests
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            // make a new file request
            const fileRequest = new DidoFileUploadRequest();
            fileRequest.file = file;
            const re = /(?:\.([^.]+))?$/;
            const regexResult = re.exec(file.name as string);
            let extension = regexResult && regexResult.length > 0 ? regexResult[1] : '';
            if (extension) {
                extension = '.' + extension.toLowerCase();
            }
            fileRequest.extension.set(extension);
            fileRequest.sizeDisplay = this.#defineSizeDisplay(file.size);

            fileRequests.push(fileRequest);
        }

        this.fileRequests.set(fileRequests);

        // we immediately upload the files (metadata is added afterwards)
        await this.#persistFiles();
    }

    async #persistFiles() {
        // keep promises for all uploads
        const uploadPromises: Promise<void>[] = [];
        // keep all responses
        const uploadModels: DocumentUploadModel[] = [];

        this.fileRequests().forEach((fileRequest) => {
            fileRequest.status.set('pending');
            // perform pre-upload validation (if not valid don't upload)
            if (!this.#validateFile(fileRequest)) {
                return;
            }

            const uploadPromise = new Promise<void>((resolve) => {
                // for now we do only upload per file so we can show a progress bar
                const formData = new FormData();
                formData.append('file', fileRequest.file, fileRequest.file.name);

                this.#http
                    .post(this.#state.didoWebUrl + 'api/upload', formData, {
                        observe: 'events',
                        reportProgress: true,
                        responseType: 'json'
                    })
                    .subscribe({
                        next: (event: any) => {
                            switch (event.type) {
                                case HttpEventType.DownloadProgress: {
                                    fileRequest.progress.set(Math.floor((event.loaded * 100) / event.total));
                                    break;
                                }
                                case HttpEventType.Response: {
                                    fileRequest.status.set('success');
                                    fileRequest.statusMessage.set(this.#i18n.get('DOMAIN_DIDO.DOCUMENTEN_CONTROL.UPLOAD_SUCCESS'));

                                    //mark as dirty
                                    event.body.dirty = true;
                                    uploadModels.push(event.body);
                                    // resolve the promise
                                    resolve();
                                }
                            }
                        },
                        error: (error: any) => {
                            // show error message
                            fileRequest.status.set('error');
                            fileRequest.statusMessage.set(this.#i18n.get('DOMAIN_DIDO.DOCUMENTEN_CONTROL.UPLOAD_ERROR'));
                            // also resolve promise on error because we need to go further
                            resolve();
                        }
                    });
            });

            uploadPromises.push(uploadPromise);
        });

        await Promise.all(uploadPromises);
        // when all promises are finished we emit the uploaded event
        if (uploadModels.length) {
            // only leave over the errors
            this.fileRequests.update((fileRequests) => fileRequests.filter((fileRequest) => fileRequest.status() === 'error'));
            // clear the file input
            if (this.typeUpload() === 'file') {
                this.fileUpload().nativeElement.value = '';
            } else {
                this.bijlageUpload().nativeElement.value = '';
            }
            // emit that a file was uploaded (document control must now do the document stuff)
            this.uploaded.emit(uploadModels);
        }
    }

    #validateFile(fileRequest: DidoFileUploadRequest): boolean {
        let validExtension = false;
        let validSize = false;

        if (fileRequest.extension() != null) {
            if (this.#intern) {
                DIDO_DOCUMENTEN_CONTROL_CONSTANTS.ALLOWED_EXTENSIONS_INTERN.forEach((allowedExtension) => {
                    if (fileRequest.extension() === allowedExtension) {
                        validExtension = true;
                    }
                });
            } else {
                DIDO_DOCUMENTEN_CONTROL_CONSTANTS.ALLOWED_EXTENIONS_EXTERN.forEach((allowedExtension) => {
                    if (fileRequest.extension() === allowedExtension) {
                        validExtension = true;
                    }
                });
            }
        }

        validSize = this.#maximumFileSizeBytes == null || fileRequest.file.size <= this.#maximumFileSizeBytes();

        if (!validExtension) {
            fileRequest.statusMessage.set(
                this.#i18n.get('DOMAIN_DIDO.DOCUMENTEN_CONTROL.VALIDATION.INVALID_EXTENSION', {
                    filename: fileRequest.file.name,
                    extensions: this.acceptExtensions
                })
            );
            fileRequest.status.set('error');
        }

        if (!validSize && validExtension) {
            fileRequest.statusMessage.set(
                this.#i18n.get('DOMAIN_DIDO.DOCUMENTEN_CONTROL.VALIDATION.TOO_LARGE_SIZE', {
                    filename: fileRequest.file.name,
                    size: this.#defineSizeDisplay(this.#maximumFileSizeBytes())
                })
            );
            fileRequest.status.set('error');
        }

        return validExtension && validSize;
    }

    #defineSizeDisplay(sizeBytes: number): string {
        let size: number;
        let sizeUnit: string;

        if (this.#maximumFileSizeBytes() >= 1024 * 1024) {
            sizeUnit = 'Mb';
            size = sizeBytes / 1024 / 1024;
        } else if (this.#maximumFileSizeBytes() >= 1024) {
            sizeUnit = 'Kb';
            size = sizeBytes / 1024;
        } else {
            sizeUnit = 'Bytes';
            size = sizeBytes;
        }

        return `${size.toFixed(2)} ${sizeUnit}`;
    }

    protected deleteFileRequest(fileRequest: DidoFileUploadRequest): void {
        this.fileRequests.update((requests) => requests.filter((request) => request.file.name !== fileRequest.file.name));
    }
}
